import { Navigate, useRoutes } from "react-router-dom";
import NotFound from "./pages/404";
import { Login } from "./pages/login";
import Home from "./pages/home";
import { PublicBase, ProtectedBase } from "./siteBase";
import AllReports from "./pages/allReports";
import { RestorePassword } from "./pages/restorePassword";
import Report from "./pages/report";
import CompanyUsers from "./pages/companyUsers";
import { CreateNewAccount } from "./pages/newAccount";
import Models from "./pages/models";
import Organizations from "./pages/organizations";
import ReportView from "./pages/reportView";
import ReportingChoice from "./pages/reportingChoice";
import AdminOrganizations from "./pages/adminPages/adminOrganizations";
import { ChangePassword } from "./pages/changePassword";
import AdminReportView from "./pages/adminPages/adminReports";
import AdminSingleReportView from "./pages/adminPages/adminReportView";
import CombinedReportView from "./pages/combinedReportView";
import BasicReport from "./pages/basicReport";
import LightReport from "./pages/lightReport";
import AdminUsers from "./pages/adminPages/adminUsers";
import AdminInvoicing from "./pages/adminPages/adminInvoicing";

// public routes that can be accessed by anyone
// mostly used for login and registration
export const publicRoutes = [
  {
    path: "/",
    element: <PublicBase />,
    children: [
      {
        path: "/",
        element: <Navigate to="/login" />,
      },
      {
        exact: true,
        path: "/register",
        element: <CreateNewAccount />,
      },
      {
        exact: true,
        path: "/login",
        element: <Login />,
      },
      {
        exact: true,
        path: "/restore-password",
        element: <RestorePassword />,
      },
      {
        exact: true,
        path: "/change-password/",
        element: <ChangePassword />,
      },
      {
        path: "*",
        element: <Navigate to="/login" />,
      },
    ],
  },
];

// protected routes that can only be accessed when logged in
export const protectedRoutes = (isAdmin: boolean, isCompanyAdmin: boolean) => {
  return [
    {
      path: "/",
      element: <ProtectedBase />,
      children: [
        {
          path: "/",
          element: <Home />,
          exact: true,
        },
        {
          path: "/reporting",
          element: isAdmin ? <AdminReportView /> : <AllReports />,
          exact: true,
        },
        {
          path: "/reporting-choice",
          element: <ReportingChoice />,
          exact: true,
        },
        {
          path: "/new-report",
          element: <Report />,
          exact: true,
        },
        {
          path: "/light-report/:reportId",
          element: <LightReport />,
          exact: true,
        },
        {
          path: "/detailed-report/:reportId",
          element: <BasicReport />,
          exact: true,
        },
        {
          path: "/report/:reportId",
          element: isAdmin ? <AdminSingleReportView /> : <Report />,
          exact: true,
        },
        {
          path: "/view-report/:reportId",
          element: isAdmin ? <AdminSingleReportView /> : <ReportView />,
          exact: true,
        },
        {
          path: "/summary-report",
          element: <CombinedReportView />,
          exact: true,
        },
        {
          path: "/models",
          element: <Models />,
          exact: true,
        },
        {
          path: "/organizations",
          element: isAdmin ? <AdminOrganizations /> : <Organizations />,
          exact: true,
        },
        // Only allow /users for admins
        ...(isCompanyAdmin
          ? [
              {
                path: "/users",
                element: <CompanyUsers />,
                exact: true,
              },
            ]
          : []),
        ...(isAdmin
          ? [
              {
                path: "/users",
                element: <AdminUsers />,
                exact: true,
              },
            ]
          : []),
        ...(isAdmin
          ? [
              {
                path: "/admin-invoicing",
                element: <AdminInvoicing />,
                exact: true,
              },
            ]
          : []),
        {
          path: "/login",
          element: <Navigate to="/" />,
          exact: true,
        },
      ],
    },
  ];
};

interface AppRoutesProps {
  isLoggedIn: boolean;
  isAdmin: boolean;
  isCompanyAdmin: boolean;
}

export const AppRoutes = ({
  isLoggedIn,
  isAdmin,
  isCompanyAdmin,
}: AppRoutesProps) => {
  const routes = isLoggedIn
    ? protectedRoutes(isAdmin, isCompanyAdmin)
    : publicRoutes;

  let element = useRoutes([...routes]);

  if (element === null) {
    element = <NotFound />;
  }

  return <>{element}</>;
};
